/**
 * ช่องวาดลายเซ็นด้วยนิ้ว → SVG (viewBox 600×180) — ใช้ร่วมกันระหว่างรับทราบข้อบังคับ (PolicyRead)
 * และลงนามรับทราบหนังสือเตือน (LetterSign · fb#176) · ยกมาจาก PolicyRead.tsx ไม่เปลี่ยนรูปแบบ SVG
 * เพราะฝั่ง worker (lib/signature-svg.ts) ตรวจว่าต้องเป็น <svg> ที่มี <path> เท่านั้น
 */
import { useRef, useState } from 'react';
import { Eraser } from 'lucide-react';

interface Point { x: number; y: number }

export function SignatureSvgPad({ onChange }: { onChange: (svg: string | null) => void }) {
  const [strokes, setStrokes] = useState<Point[][]>([]);
  const active = useRef<Point[] | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);

  function point(event: React.PointerEvent<SVGSVGElement>): Point {
    const rect = event.currentTarget.getBoundingClientRect();
    return {
      x: Math.round(((event.clientX - rect.left) / rect.width) * 6000) / 10,
      y: Math.round(((event.clientY - rect.top) / rect.height) * 1800) / 10,
    };
  }

  function down(event: React.PointerEvent<SVGSVGElement>) {
    event.currentTarget.setPointerCapture(event.pointerId);
    active.current = [point(event)];
    setStrokes((current) => [...current, active.current!]);
  }

  function move(event: React.PointerEvent<SVGSVGElement>) {
    if (!active.current) return;
    active.current.push(point(event));
    setStrokes((current) => [...current]);
  }

  function finish() {
    if (!active.current) return;
    active.current = null;
    const paths = strokes.filter((stroke) => stroke.length > 1).map(pathData);
    onChange(paths.length > 0 ? serializeSvg(paths) : null);
  }

  function clear() {
    active.current = null;
    setStrokes([]);
    onChange(null);
  }

  const paths = strokes.filter((stroke) => stroke.length > 1).map(pathData);
  return (
    <div className="space-y-2">
      <svg
        ref={svgRef}
        viewBox="0 0 600 180"
        className="w-full h-40 border-2 border-dashed border-gray-300 rounded-lg bg-white touch-none"
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={finish}
        onPointerCancel={finish}
        onPointerLeave={finish}
        aria-label="ช่องวาดลายเซ็น"
      >
        {paths.map((path, index) => (
          <path key={index} d={path} fill="none" stroke="#111827" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        ))}
      </svg>
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs text-gray-500">วาดลายเซ็นด้วยนิ้วในกรอบ</span>
        <button type="button" className="btn-secondary min-h-11" onClick={clear}>
          <Eraser className="w-4 h-4" /> ล้างลายเซ็น
        </button>
      </div>
    </div>
  );
}

function pathData(points: Point[]): string {
  return points.map((point, index) => `${index === 0 ? 'M' : 'L'}${point.x} ${point.y}`).join(' ');
}

function serializeSvg(paths: string[]): string {
  const body = paths.map((path) => `<path d="${path}" fill="none" stroke="#111827" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>`).join('');
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 600 180">${body}</svg>`;
}
